import { useMemo } from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart
} from 'recharts';

// Metric configurations with normalization
const metricConfig = {
  adoptionRate: {
    key: 'adoptionRate',
    name: 'Adoption Rate',
    color: '#10b981', // green
    yAxisId: 'metrics',
    stdKey: 'stdAdoptionRate'
  },
  atiScore: {
    key: 'avgATIScore',
    name: 'ATI Score',
    color: '#8b5cf6', // purple
    yAxisId: 'metrics',
    stdKey: 'stdATIScore'
  },
  aiKnowledge: {
    key: 'avgAIKnowledge',
    name: 'AI Knowledge',
    color: '#3b82f6', // blue
    yAxisId: 'metrics',
    stdKey: 'stdAIKnowledge'
  },
  optimismScore: {
    key: 'avgOptimismScore',
    name: 'Optimism Score',
    color: '#f59e0b', // amber
    yAxisId: 'metrics',
    stdKey: 'stdOptimismScore'
  },
  trust: {
    key: 'trust',
    name: 'AI Trust',
    color: '#06b6d4', // cyan
    yAxisId: 'metrics',
    stdKey: 'stdTrust'
  },
  fear: {
    key: 'fear',
    name: 'AI Fear',
    color: '#ef4444', // red
    yAxisId: 'metrics',
    stdKey: 'stdFear'
  },
  learningInterest: {
    key: 'learningInterest',
    name: 'Learning Interest',
    color: '#ec4899', // pink
    yAxisId: 'metrics',
    stdKey: 'stdLearningInterest'
  },
  responseCount: {
    key: 'cumulativeResponses',
    name: 'Total Responses',
    color: '#6b7280', // gray
    yAxisId: 'responses'
  }
};

export default function TimeSeriesChart({ data, metrics = [], showResponseCount = true, showStdDev = true, metricConfigs }) {
  // Use custom configs if provided, otherwise use built-in defaults
  const resolvedConfig = metricConfigs || metricConfig;
  // Format date for tooltip
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Get the data point to access std dev values
      const dataPoint = chartData.find(d => d.date === label);

      return (
        <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-semibold text-text-primary-light dark:text-text-primary mb-2">
            {formatDate(label)}
          </p>
          {payload.map((entry, index) => {
            // Skip std dev areas and range values in tooltip
            if (entry.dataKey && (entry.dataKey.startsWith('std') || entry.dataKey.includes('_'))) return null;

            // Find the metric config for this entry
            const metric = activeMetrics.find(m => m.key === entry.dataKey);

            // Format the value
            let displayText = `${entry.name}: ${typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}`;

            // Add std dev if available and showStdDev is true
            if (showStdDev && metric && metric.stdKey && dataPoint && dataPoint[metric.stdKey]) {
              const stdValue = dataPoint[metric.stdKey].toFixed(1);
              displayText += ` ± ${stdValue}`;
            }

            return (
              <p
                key={index}
                className="text-sm"
                style={{ color: entry.color }}
              >
                {displayText}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Format X-axis date
  const formatXAxis = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Determine which metrics to show
  const activeMetrics = useMemo(() => {
    const metricsToShow = metrics.map(m => resolvedConfig[m]).filter(Boolean);
    if (showResponseCount) {
      metricsToShow.push(resolvedConfig.responseCount || metricConfig.responseCount);
    }
    return metricsToShow;
  }, [metrics, showResponseCount, resolvedConfig]);

  // Ensure data is an array
  const chartData = Array.isArray(data) ? data : [];

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-text-muted-light dark:text-text-muted">
        No time series data available
      </div>
    );
  }

  // Calculate upper and lower bounds for std dev areas
  const dataWithBounds = chartData.map(point => {
    const enhanced = { ...point };

    activeMetrics.forEach(metric => {
      if (metric.stdKey && showStdDev) {
        const value = point[metric.key] || 0;
        const std = point[metric.stdKey] || 0;
        enhanced[`${metric.key}_upper`] = Math.min(100, value + std);
        enhanced[`${metric.key}_lower`] = Math.max(0, value - std);
        enhanced[`${metric.key}_range`] = [enhanced[`${metric.key}_lower`], enhanced[`${metric.key}_upper`]];
      }
    });

    return enhanced;
  });

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart
        data={dataWithBounds}
        margin={{ top: 5, right: 60, left: 60, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />

        <XAxis
          dataKey="date"
          tickFormatter={formatXAxis}
          stroke="#9ca3af"
          className="dark:stroke-gray-400"
          style={{ fontSize: '12px' }}
        />

        {/* Left Y-axis for response count */}
        {showResponseCount && (
          <YAxis
            yAxisId="responses"
            stroke="#6b7280"
            className="dark:stroke-gray-400"
            style={{ fontSize: '12px' }}
            label={{ value: 'Responses', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fill: '#6b7280' } }}
          />
        )}

        {/* Right Y-axis for normalized metrics (0-100) */}
        <YAxis
          yAxisId="metrics"
          orientation="right"
          stroke="#9ca3af"
          className="dark:stroke-gray-400"
          style={{ fontSize: '12px' }}
          domain={[0, 100]}
          label={{ value: 'Score (0-100)', angle: 90, position: 'insideRight', style: { fontSize: '12px', fill: '#9ca3af' } }}
        />

        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: '12px' }}
          iconType="line"
        />

        {/* Render standard deviation areas first (so they're behind the lines) */}
        {showStdDev && activeMetrics.map((metric) => {
          if (!metric.stdKey) return null;

          return (
            <Area
              key={`${metric.key}_std`}
              yAxisId="metrics"
              type="monotone"
              dataKey={`${metric.key}_range`}
              fill={metric.color}
              stroke="none"
              fillOpacity={0.1}
              name={`${metric.name} ±σ`}
              legendType="none"
            />
          );
        })}

        {/* Render lines for each active metric */}
        {activeMetrics.map((metric) => (
          <Line
            key={metric.key}
            yAxisId={metric.yAxisId}
            type="monotone"
            dataKey={metric.key}
            name={metric.name}
            stroke={metric.color}
            strokeWidth={2}
            dot={{ fill: metric.color, r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
